import { Award, CheckCircle, Flame, Trophy, type LucideIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import type { GamificationProfile } from '@/types';

const BADGE_ICONS: Record<string, LucideIcon> = {
  Flame,
  Award,
  CheckCircle,
  Trophy,
};

function BadgeGlyph({ name }: { name: string | null }) {
  const Icon = (name && BADGE_ICONS[name]) || Trophy;
  return <Icon className="h-4 w-4 shrink-0 text-foreground dark:text-muted" aria-hidden />;
}

export function GamificationProgressCard({
  profile,
  loading,
  className,
}: {
  profile: GamificationProfile | null;
  loading: boolean;
  className?: string;
}) {
  const pct =
    profile && profile.xp_to_next_level > 0
      ? Math.min(100, Math.round((profile.xp_into_level / profile.xp_to_next_level) * 100))
      : 0;

  return (
    <Card className={cn('card-elevated section-enter overflow-hidden', className)}>
      <CardHeader className="border-b border-border pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Trophy className="h-4 w-4 text-warning" aria-hidden />
          Fortschritt
        </CardTitle>
        <p className="text-xs text-muted">
          XP durch Login, erledigte Aufgaben und abgeschlossene Aktivitäten.
        </p>
      </CardHeader>
      <CardContent className="space-y-4 pt-4">
        {loading && !profile ? (
          <div className="space-y-2">
            <div className="h-8 w-32 animate-pulse rounded bg-card-hover" />
            <div className="h-2 w-full animate-pulse rounded bg-card-hover" />
          </div>
        ) : profile ? (
          <>
            <div className="flex flex-wrap items-end justify-between gap-2">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted">
                  Level
                </p>
                <p className="text-3xl font-bold tabular-nums text-foreground">{profile.level}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted">Gesamt-XP</p>
                <p className="text-lg font-semibold tabular-nums text-foreground dark:text-muted">
                  {profile.total_xp}
                </p>
              </div>
            </div>
            <div>
              <div className="mb-1 flex justify-between text-xs text-muted">
                <span>
                  Nächstes Level: {profile.xp_into_level} / {profile.xp_to_next_level} XP
                </span>
              </div>
              <Progress value={pct} className="h-2" />
            </div>
            <div className="flex flex-wrap gap-4 rounded-lg bg-background px-3 py-2 dark:bg-card-hover/80">
              <div>
                <p className="text-xs text-muted">Login-Serie</p>
                <p className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                  <Flame className="h-4 w-4 text-warning" aria-hidden />
                  {profile.streak_current} Tage
                  {profile.streak_best > 0 && (
                    <span className="font-normal text-muted">
                      (Best: {profile.streak_best})
                    </span>
                  )}
                </p>
              </div>
            </div>
            {profile.badges.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-medium text-muted">Badges</p>
                <ul className="flex flex-wrap gap-2">
                  {profile.badges.slice(0, 8).map((b) => (
                    <li
                      key={`${b.slug}-${b.earned_at}`}
                      title={b.description ?? b.title}
                      className="flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-xs font-medium text-muted dark:text-foreground"
                    >
                      <BadgeGlyph name={b.lucide_icon} />
                      {b.title}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-muted">Fortschritt konnte nicht geladen werden.</p>
        )}
      </CardContent>
    </Card>
  );
}
